import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Bell, LogOut, Home } from "lucide-react";
import kleidoLogo from "../../../assets/images/kleido-logoonly.png";
import GlobalSearch from "../GlobalSearch/GlobalSearch.js";
import NotificationDropdown from "./NotificationDropdown";
import ProductList from "./ProductDropDown.js";

const Navbar = () => {
  const navigate = useNavigate();
  const dropdownRef = useRef(null);

  // ── State ──
  const [userid, setUserid] = useState(null);
  const [showSearchDropdown, setShowSearchDropdown] = useState(false);
  const [showNotificationDropdown, setShowNotificationDropdown] = useState(false);
  const [notifications, setNotifications] = useState([]);

  // ==================== CHECK LOCALSTORAGE ====================
  useEffect(() => {
    try {
      const raw = localStorage.getItem("userLoggedin");
      if (raw) {
        const parsed = JSON.parse(raw);
        const id = parsed?.userid || parsed?.id || parsed?.userId;
        if (id) {
          setUserid(id);
          console.log("✅ [Navbar] Logged-in user found:", id);
        } else {
          console.warn("⚠️ [Navbar] userLoggedin found but no userId");
        }
      }
    } catch (err) {
      console.error("❌ [Navbar] Error reading localStorage:", err);
    }
  }, []);

  // ==================== FETCH NOTIFICATIONS ====================
  useEffect(() => {
    if (!userid) return;

    const fetchNotifications = async () => {
      try {
        const res = await fetch(
          `http://localhost:3010/api/notifications/${userid}`
        );
        if (res.ok) {
          const data = await res.json();
          setNotifications(Array.isArray(data) ? data : data.notifications || []);
        }
      } catch (err) {
        console.error("❌ [Navbar] Failed to fetch notifications:", err);
        try {
          const stored = localStorage.getItem("notifications");
          if (stored) setNotifications(JSON.parse(stored));
        } catch (_) {
          /* ignore */
        }
      }
    };

    fetchNotifications();
  }, [userid]);

  // ==================== CLOSE ON OUTSIDE CLICK ====================
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setShowSearchDropdown(false);
        setShowNotificationDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // ==================== HANDLERS ====================

  const handleLogoClick = () => {
      window.location.href = `http://kleido.ai/user-feed/${userid}`;
  };

  const handleProfileHome = () => {
    if (userid) {
      navigate(`/home/${userid}`);
    }
  };

  const handleSignOut = () => {
    window.location.href = "http://kleido.ai/signout";
  };

  const toggleSearchDropdown = () => {
    setShowSearchDropdown((prev) => !prev);
    setShowNotificationDropdown(false);
  };

  const toggleNotificationDropdown = () => {
    setShowNotificationDropdown((prev) => !prev);
    setShowSearchDropdown(false);
  };

  // ==================== RENDER ====================

  return (
    <nav
      className="sticky top-0 z-50 border-b border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80"
      style={{
        background: "var(--card-bg, rgb(4, 4, 4))",
        borderBottom: "1px solid var(--border-color, rgb(52, 51, 51))",
      }}
    >
      <div className="mx-auto flex h-14 max-w-6xl items-center justify-between px-4">

        {/* ── LEFT: Logo + Events Text ── */}
        <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
          <button
            onClick={handleLogoClick}
            className="flex items-center gap-2 shrink-0"
            style={{ cursor: "pointer" }}
            aria-label="Go to home"
          >
            <img src={kleidoLogo} alt="Kleido AI" className="h-10 w-10 rounded-lg" />
          </button>

          <span
            style={{
              fontWeight: "bold",
              color: "white",
              fontSize: "10px",
              marginTop: 30,
              marginLeft: -3
            }}
          >
            Gather
          </span>
        </div>

        {/* ── RIGHT: Search, Notification, Home, Sign Out ── */}
        <div className="flex items-center gap-2" ref={dropdownRef}>

          {/* Search */}
          <button
            onClick={toggleSearchDropdown}
            className="rounded-lg p-2 text-muted-foreground hover:bg-secondary hover:text-foreground transition-colors"
            title="Search"
            style={{ color: "white" }}
          >
            <Search size={18} />
          </button>

          {/* Notification */}
          <button
            onClick={toggleNotificationDropdown}
            className="relative rounded-lg p-2 text-muted-foreground hover:bg-secondary hover:text-foreground transition-colors"
            title="Notifications"
            style={{ color: "white" }}
          >
            <Bell size={18} />
            {notifications.length > 0 && (
              <span
                className="absolute right-1 top-1 flex h-4 w-4 items-center justify-center rounded-full text-[10px] font-bold"
                style={{ background: "yellow", color: "black", marginRight: -5 }}
              >
                {notifications.length}
              </span>
            )}
          </button>

          {/* Home */}
          <button
            onClick={handleProfileHome}
            className="rounded-lg p-2 text-muted-foreground hover:bg-secondary hover:text-foreground transition-colors"
            title="Home"
            style={{ color: "white" }}
          >
            <Home size={18} />
          </button>

          <ProductList />  

          {/* Sign Out */}
          <button
            onClick={handleSignOut}
            className="rounded-lg p-2 text-muted-foreground hover:bg-destructive/20 hover:text-destructive transition-colors"
            title="Sign Out"
            style={{ color: "white" }}
          >
            <LogOut size={18} />
          </button>

        </div>
      </div>

      {/* ── Search Dropdown ── */}
      {showSearchDropdown && (
        <div className="nb-search-dropdown" ref={dropdownRef}>
          <GlobalSearch />
        </div>
      )}

      {/* ── Notification Dropdown ── */}
      {showNotificationDropdown && (
        <NotificationDropdown
          notifications={notifications}
          onClose={() => setShowNotificationDropdown(false)}
        />
      )}
    </nav>
  );
};

export default Navbar;
